import { queryOptions } from "@tanstack/react-query";

import type { BranchingGateway } from "~/branching/data-source/port/branching-gateway";
import type { ArchiveStatus } from "~/branching/model/archive-status";
import type { BranchId } from "~/branching/model/branch";
import { DEFAULT_CACHE_TIME } from "~/constants";

/**
 * Hierarchical query keys: invalidate `system(systemId)` to blanket-clear branching for a system.
 * Dual-invalidation with legacy `systems` keys is handled in `use-branching-mutations` until reads migrate.
 */
export const branchingQueryKeys = {
  all: ["branching"] as const,
  system: (systemId: string) => [...branchingQueryKeys.all, systemId] as const,
  branches: (systemId: string) => [...branchingQueryKeys.system(systemId), "branches"] as const,
  branch: (systemId: string, branchId: string | undefined) =>
    [...branchingQueryKeys.system(systemId), "branch", branchId] as const,
  history: (systemId: string, branchId: string | undefined, headCommitId?: string) => {
    const base = [...branchingQueryKeys.system(systemId), "history", branchId] as const;
    return headCommitId ? ([...base, headCommitId] as const) : base;
  },
  // Deliberately omits headCommitId -- CR tab shows full branch history and does not need head-relative refetch triggering.
  branchHistory: (systemId: string, branchId: string | undefined) =>
    [...branchingQueryKeys.branch(systemId, branchId), "history"] as const,
  branchSummary: (systemId: string, branchId: string | undefined) =>
    [...branchingQueryKeys.branch(systemId, branchId), "summary"] as const,
  headCommitMeta: (systemId: string, branchId: BranchId) =>
    [...branchingQueryKeys.branch(systemId, branchId), "head-commit-meta"] as const,
  branchList: (systemId: string) => [...branchingQueryKeys.system(systemId), "list-items"] as const,
  listItems: (systemId: string, archiveStatuses: ArchiveStatus[]) =>
    [...branchingQueryKeys.branchList(systemId), archiveStatuses] as const,
  listItemsAll: (systemId: string) => [...branchingQueryKeys.branchList(systemId), "all"] as const,
  branchCrList: (systemId: string) => [...branchingQueryKeys.system(systemId), "branch-cr-list"] as const,
};

export function branchSummaryQueryOptions(
  gateway: BranchingGateway,
  systemId: string | undefined,
  branchId: BranchId | undefined,
) {
  return queryOptions({
    queryKey: branchingQueryKeys.branchSummary(systemId!, branchId),
    queryFn: () => gateway.getBranchSummary(branchId!),
    staleTime: DEFAULT_CACHE_TIME,
  });
}

export function branchHistoryQueryOptions(gateway: BranchingGateway, systemId: string, branchId: BranchId) {
  return queryOptions({
    queryKey: branchingQueryKeys.branchHistory(systemId, branchId),
    queryFn: () => gateway.getCommitHistory(branchId),
    staleTime: DEFAULT_CACHE_TIME,
  });
}
